/**
 * useLeadStatusOptions
 *
 * Backend endpoint'ten 24 lead status seçeneğini çeker.
 * Sonuç modül düzeyinde önbelleğe alınır — uygulama yaşam döngüsü
 * boyunca tek fetch yapılır.
 *
 * Dönüş değeri:
 *   { options: [{value, label}], loading, error }
 *
 * leadStatusLabel(value, options) — yardımcı fonksiyon:
 *   Bilinmeyen slug için value.toUpperCase() döner (eski kayıtlar patlmasın).
 *
 * LEAD_STATUS_COLOR_MAP — semantik renk grupları:
 *   Rozet arka planı + metin rengi için Tailwind class çiftleri.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modül düzeyinde önbellek
let _cache = null;          // [{value, label}]
let _promise = null;        // devam eden fetch varsa aynı promise'i bekle

async function fetchOptions() {
  if (_cache) return _cache;
  if (_promise) return _promise;

  _promise = axios
    .get(`${API}/admin/leads/status-options`)
    .then((r) => {
      _cache = r.data.options || [];
      _promise = null;
      return _cache;
    })
    .catch((err) => {
      _promise = null;
      console.warn('[useLeadStatusOptions] status-options endpoint hatası:', err?.message || err);
      return [];
    });

  return _promise;
}

export function useLeadStatusOptions() {
  const [options, setOptions] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (_cache) {
      setOptions(_cache);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchOptions()
      .then((opts) => {
        setOptions(opts);
        setError(null);
      })
      .catch((err) => {
        setError(err);
        setOptions([]);
      })
      .finally(() => setLoading(false));
  }, []);

  return { options, loading, error };
}

/**
 * leadStatusLabel
 * options dizisinde bulunan slug için backend'den gelen etiketi döner.
 * Bilinmeyen slug için value'yu BÜYÜK HARF yapar (eski kayıtlar patlamasın).
 *
 * "donusturuldu" özel durum: backend status-options listesinde YOK (manuel
 * seçilemeyen bir sistem statüsü — satış onay akışında otomatik atanır),
 * bu yüzden options'ta bulunamayıp ham slug "DONUSTURULDU" basıyordu.
 */
export function leadStatusLabel(value, options) {
  if (!value) return '-';
  if (value === 'donusturuldu') return 'Kayıt Tamamlandı';
  if (!options || options.length === 0) return value.toUpperCase();
  const found = options.find((o) => o.value === value);
  return found ? found.label : value.toUpperCase();
}

// ─── Semantik renk grupları ───────────────────────────────────────────────────
// Her grup: { bg, text, border } — Tailwind class string'leri
//
// OLUMLU (yeşil)    : kayit_asamasi_ayni_gun, kayit_asamasi_ileri_tarih,
//                     kendisi_arayacak_olumlu, kuruma_gelecek
// OLUMSUZ (kırmızı) : istemiyor, fake_basvuru, olumsuz_wp, yanlis_basvuru,
//                     numarami_engelledi, sms_iptal, sikintili, ucretsiz_sandim
// NÖTR-TAKİP (amber): cevap_yok, mesgul, musait_degil, kapali, no_servis_disi,
//                     dusunecek, karar_aramasi_cy, fiyat_fazla,
//                     kendisi_arayacak_olumsuz, genel_gorusme
// SÜREÇ (mavi)      : baska_danismanda, baskasina_ata
// YENİ (gri)        : yeni (ve bilinmeyenler)
//
// NOT (2026-08 durum sözlüğü düzeltmesi): kayit_asamasi_on_kayit,
// kayit_asamasi_sozlesme, kayit_asamasi_odeme, kayit_asamasi_tamamlandi
// backend'de OLMAYAN hayalet slug'lardı (services/lead_statuses.py canonical
// listesinde yok) — kaldırıldı. Gerçek kayıt-aşaması slug'ları
// kayit_asamasi_ayni_gun / kayit_asamasi_ileri_tarih'tir; emerald DEĞİL
// green kullanılır ki kendisi_arayacak_olumlu'dan (emerald) ayrışsın.
// fiyat_fazla kırmızıdan amber'e taşındı — hâlâ takipte olan bir lead için
// "kapalı/olumsuz" yanlış sinyali vermesin diye.
//
export const LEAD_STATUS_COLOR_MAP = {
  // olumlu
  kayit_asamasi_ayni_gun:     { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  kayit_asamasi_ileri_tarih:  { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  kendisi_arayacak_olumlu:    { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
  kuruma_gelecek:              { bg: 'bg-green-100',   text: 'text-green-700',   border: 'border-green-200' },

  // olumsuz
  istemiyor:           { bg: 'bg-red-100',  text: 'text-red-700',  border: 'border-red-200' },
  fake_basvuru:        { bg: 'bg-red-100',  text: 'text-red-700',  border: 'border-red-200' },
  olumsuz_wp:          { bg: 'bg-red-100',  text: 'text-red-700',  border: 'border-red-200' },
  yanlis_basvuru:      { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200' },
  numarami_engelledi:  { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200' },
  sms_iptal:           { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200' },
  sikintili:           { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200' },
  ucretsiz_sandim:     { bg: 'bg-red-100',  text: 'text-red-700',  border: 'border-red-200' },

  // nötr-takip
  cevap_yok:                  { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  mesgul:                     { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  musait_degil:                { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  kapali:                      { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  no_servis_disi:              { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  dusunecek:                   { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  karar_aramasi_cy:            { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  fiyat_fazla:                 { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  kendisi_arayacak_olumsuz:    { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  genel_gorusme:                { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },

  // süreç
  baska_danismanda: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  baskasina_ata:    { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },

  // yeni / fallback
  yeni: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' },

  // sistem statüsü — satış onaylandı, öğrenciye dönüştürüldü (manuel seçilemez)
  donusturuldu: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
};

/** Renk sınıflarını döner; bilinmeyenler için varsayılan gri. */
export function leadStatusColors(value) {
  return LEAD_STATUS_COLOR_MAP[value] || { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' };
}
